import { Component, OnInit } from '@angular/core';
import { QuoteDetailGeneralInformation } from '../quote-detail.models';
import {
	CustomFormAccessor,
	FormConfig,
} from 'src/shared/form-models/form-cva.component';
import { NgControl, FormBuilder, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import {
	JobQuoteStatus,
	CustomerLink,
	RailroadLink,
	Division,
	OfferedService,
	JobTrackID,
	RailroadOvertimeBasis,
	RailroadRegularTimeBasis,
	JobParty,
	JobType,
	JobEndMarket,
} from 'src/shared/api';
import {
	buildArrayFromEnum,
	idComparer,
} from 'src/shared/utilities/utility-functions';
import {
	displayQuoteStatus,
	displayName,
	displayJobTrackID,
	displayEndMarket,
	displayJobType,
} from 'src/shared/enums';
import { QuoteDetailPartialState } from '../+state/quote-detail.reducer';
import * as QuoteDetailActions from '../+state/quote-detail.actions';
import * as QuoteDetailSelectors from '../+state/quote-detail.selectors';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import { MatSelectChange } from '@angular/material/select';

const quoteGeneralInformationFormConfig: FormConfig<QuoteDetailGeneralInformation> =
	{
		id: [''],
		flairDocsJobId: [''],
		number: [''],
		customer: ['', [Validators.required]],
		railroad: ['', [Validators.required]],
		division: [''],
		subdivision: [''],
		regularTimeBasis: [''],
		overTimeBasis: [''],
		offeredServices: [''],
		team: ['', [Validators.required]],
		start: [''],
		end: [''],
		approvalDate: [''],
		permitNumber: [''],
		quoteStatus: [''],
		isSafetyTrainingComplete: [''],
		descriptionOfSchedule: [''],
		equipmentDescription: [''],
		description: ['', [Validators.required]],
		notes: [''],
		originatingPortalUserEmail: [''],
		quoteNotificationRecipients: [''],
		dwrNotificationRecipients: [''],
		jobSetupFormRefreshDate: [''],
		status: [''],
		trackSupervisor: [''],
		dateCompleted: [''],
		lastInvoiceCutoffDate: [''],
		assignments: [''],
		unassignedShifts: [''],
		railroadSubCode: [''],
		dateRange: [''],
		isNonBillable: [''],
		orderNumber: [''],
		trackID: [''],
		party: ['', [Validators.required]],
		jobType: ['', [Validators.required]],
		endMarket: ['', [Validators.required]],
		daysRequested: [
			'',
			[Validators.required, Validators.min(0), Validators.max(15)],
		],
	};

const formOptions = {
	validators: [CustomValidators.endDateNotBeforeStart],
};

@Component({
	selector: 'rpfsa-quote-general-info',
	templateUrl: './general-info.component.html',
})
export class QuoteDetailGeneralInfoComponent
	extends CustomFormAccessor<QuoteDetailGeneralInformation>
	implements OnInit
{
	readonly divisions$ = this.store.pipe(
		select(QuoteDetailSelectors.getDivisionOptions)
	);
	readonly offeredServices$ = this.store.pipe(
		select(QuoteDetailSelectors.getOfferedServiceOptions)
	);

	readonly requireJobDates$ = this.store.pipe(
		select(QuoteDetailSelectors.getRequireJobDates)
	);

	readonly quoteStatuses = buildArrayFromEnum(JobQuoteStatus);

	readonly overtimeBasisTypes = buildArrayFromEnum(RailroadOvertimeBasis);
	readonly regularTimeBasisTypes = buildArrayFromEnum(
		RailroadRegularTimeBasis
	);

	readonly displayQuoteStatus = displayQuoteStatus;
	readonly displayName = displayName;
	readonly idComparer = idComparer;

	readonly jobTrackIDs = buildArrayFromEnum(JobTrackID);
	readonly displayJobTrackID = displayJobTrackID;
	// RP-3404: Refactor CN-hardcoded Track ID and Order Number fields
	readonly showTrackID$ = this.store.pipe(
		select(QuoteDetailSelectors.getRailroadForQuote),
		filter((rail) => !!rail),
		map((rail) => rail.showTrackID)
	);
	readonly showOrderNumber$ = this.store.pipe(
		select(QuoteDetailSelectors.getRailroadForQuote),
		filter((rail) => !!rail),
		map((rail) => rail.showOrderNumber)
	);
	readonly jobPartyTypes = buildArrayFromEnum(JobParty);
	readonly typesOfWork = buildArrayFromEnum(JobType);
	readonly endMarket = buildArrayFromEnum(JobEndMarket);
	readonly displayEndMarket = displayEndMarket;
	readonly displayJobType = displayJobType;

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		private readonly store: Store<QuoteDetailPartialState>
	) {
		super(ngControl, fb, quoteGeneralInformationFormConfig, formOptions);
	}

	ngOnInit() {
		this.store
			.pipe(
				select(QuoteDetailSelectors.getOvertimeBasis),
				debounceTime(500),
				takeUntil(this.destroyed)
			)
			.subscribe((overtimeBasis) =>
				this.form.get('overTimeBasis').setValue(overtimeBasis)
			);
		this.store
			.pipe(
				select(QuoteDetailSelectors.getRegularTimeBasis),
				debounceTime(500),
				takeUntil(this.destroyed)
			)
			.subscribe((regularTimeBasis) =>
				this.form.get('regularTimeBasis').setValue(regularTimeBasis)
			);

		super.ngOnInit();
	}

	customerSelected(customer: CustomerLink): void {
		if (customer) {
			this.store.dispatch(
				QuoteDetailActions.customerSelected({ customerId: customer.id })
			);
		}
	}

	railroadSelected(railroad: RailroadLink): void {
		if (railroad) {
			this.store.dispatch(
				QuoteDetailActions.railroadSelected({ railroadId: railroad.id })
			);
			// RP-2110: reset Order # and Track ID if railroad is other than CN
			if (railroad.showTrackID) {
				this.form.get('trackID').reset(JobTrackID.Main);
			} else {
				this.form.get('trackID').reset('');
			}
			if (!railroad.showOrderNumber) {
				this.form.get('orderNumber').reset('');
			}
		} else {
			this.form.get('overTimeBasis').reset('');
			this.form.get('regularTimeBasis').reset('');
		}

		this.form.get('division').reset('');
	}

	divisionSelected(division: Division): void {
		this.store.dispatch(QuoteDetailActions.divisionSelected({ division }));
	}

	offeredServiceChange(change: MatSelectChange) {
		this.store.dispatch(
			QuoteDetailActions.offeredServicesUpdated({
				offeredServices: change.value as OfferedService[],
			})
		);
	}

	// Right now offeredServiceChange is triggered on each click.
	// This is a sort of "debounce" of that, so it only goes on blur.
	offeredServicesToggle() {
		this.store.dispatch(QuoteDetailActions.offeredServicesToggled());
	}
}
